/**
 * Counting the moments the product actually delivered something.
 *
 * The traffic counter in the Worker answers "did anyone arrive". This answers
 * the question after it, which is the one that decides whether any of the
 * marketing is working: **did they leave with a file?** Between a page view and
 * a saved account there is currently nothing, so a page can send a thousand
 * people to an editor that loses all of them and every number still looks fine.
 *
 * ## Why a beacon here when the traffic counter needed none
 *
 * An export finishes in the browser — canvas to blob to a download link. The
 * server is never involved, so there is no request for the edge to count. This
 * is the one measurement that genuinely cannot be taken server-side.
 *
 * `sendBeacon` rather than `fetch`, because it is built for exactly this: the
 * browser takes ownership of the request and completes it even if the page is
 * navigating away, and it cannot delay anything the user is doing. A download
 * often *is* a navigation, so a plain fetch would be the request most likely to
 * be cancelled.
 *
 * Same-origin, so `connect-src 'self'` already allows it and the CSP does not
 * move. No vendor, no cookie, no id.
 *
 * ## What is sent
 *
 * One word from a fixed list, and nothing else. No design, no filename, no
 * dimensions, no identifier — the server counts occurrences per day and could
 * not tell two people apart if it wanted to. That is the same bargain the
 * traffic counter makes, and it is what keeps this outside the privacy policy's
 * promises rather than in tension with them.
 */

import type { UsageEvent } from "../../shared/usage-events";

/**
 * Count one occurrence. Never throws, never blocks, never delays a download.
 *
 * Deliberately returns nothing. There is no success to await and no failure
 * worth handling: a measurement that can interrupt an export is not worth
 * taking, and the export is the thing the user came for.
 */
export function countUsage(event: UsageEvent): void {
  try {
    const body = JSON.stringify({ event });
    // `navigator` is absent under the node test environment, and a counter must
    // not be the reason a test suite cannot import a module.
    const beacon = globalThis.navigator?.sendBeacon?.bind(globalThis.navigator);
    if (beacon) {
      beacon("/api/event", new Blob([body], { type: "application/json" }));
      return;
    }
    // Older Safari has no sendBeacon. `keepalive` is the fetch equivalent and
    // survives the page going away for the same reason.
    void fetch("/api/event", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // Deliberately silent. See above.
  }
}
